/**
 * Planning resource APIs (MS Project-style resources).
 *
 * GET/POST  dpr/apis/planning/resource
 * PUT/DELETE dpr/apis/planning/resource/{resourceId}
 */
import { notifications } from '@mantine/notifications';
import { showErrorNotification } from '../utils/utils';
import { transactionAxios } from './schedulingApis';

const successNotificationColor = 'teal';

export const PLANNING_RESOURCE_TYPES = ['WORK', 'MATERIAL', 'COST'] as const;
export type PlanningResourceType = (typeof PLANNING_RESOURCE_TYPES)[number];

export interface PlanningResource {
  id: string;
  projectId: string;
  name: string;
  type: PlanningResourceType | string;
  unitOfMeasure: string;
  maxUnits: number;
  maxUnitsPerDay: number;
  standardRate: number;
  overtimeRate: number;
  costPerUse: number;
  initials: string;
  email: string | null;
  calendarId: string;
  notes: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  meta: Record<string, unknown> | null;
}

export interface CreatePlanningResourceRequest {
  id?: string;
  projectId: string;
  name: string;
  type: PlanningResourceType | string;
  unitOfMeasure: string;
  maxUnits?: number;
  maxUnitsPerDay?: number;
  standardRate?: number;
  overtimeRate?: number;
  costPerUse?: number;
  initials?: string;
  email?: string | null;
  calendarId?: string;
  notes?: string | null;
  meta?: Record<string, unknown> | null;
}

export interface UpdatePlanningResourceRequest {
  projectId: string;
  name: string;
  type: PlanningResourceType | string;
  unitOfMeasure: string;
  maxUnits: number;
  maxUnitsPerDay: number;
  standardRate: number;
  overtimeRate: number;
  costPerUse: number;
  initials: string;
  email?: string | null;
  calendarId: string;
  notes?: string | null;
  meta?: Record<string, unknown> | null;
}

function asArray(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (value != null && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    for (const key of ['data', 'payload', 'result']) {
      if (Array.isArray(record[key])) return record[key] as unknown[];
    }
  }
  return [];
}

function unwrapEnvelope(raw: unknown): unknown {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return raw;
  const record = raw as Record<string, unknown>;
  if (record.data != null) return record.data;
  if (record.payload != null) return record.payload;
  return raw;
}

function toNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

function optionalString(value: unknown): string | null {
  if (value == null) return null;
  const s = String(value).trim();
  return s === '' ? null : s;
}

export function parsePlanningResource(raw: unknown): PlanningResource | null {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  const id = String(row.id ?? '').trim();
  const projectId = String(row.projectId ?? row.project_id ?? '').trim();
  const name = String(row.name ?? '').trim();
  if (!id || !projectId) return null;

  const metaRaw = row.meta;

  return {
    id,
    projectId,
    name,
    type: String(row.type ?? '').trim() || 'WORK',
    unitOfMeasure: String(row.unitOfMeasure ?? row.unit_of_measure ?? '').trim() || 'h',
    maxUnits: toNumber(row.maxUnits ?? row.max_units, 0),
    maxUnitsPerDay: toNumber(row.maxUnitsPerDay ?? row.max_units_per_day, 0),
    standardRate: toNumber(row.standardRate ?? row.standard_rate, 0),
    overtimeRate: toNumber(row.overtimeRate ?? row.overtime_rate, 0),
    costPerUse: toNumber(row.costPerUse ?? row.cost_per_use, 0),
    initials: String(row.initials ?? '').trim(),
    email: optionalString(row.email),
    calendarId: String(row.calendarId ?? row.calendar_id ?? '').trim(),
    notes: optionalString(row.notes),
    createdAt:
      row.createdAt != null || row.created_at != null
        ? String(row.createdAt ?? row.created_at)
        : null,
    updatedAt:
      row.updatedAt != null || row.updated_at != null
        ? String(row.updatedAt ?? row.updated_at)
        : null,
    meta:
      metaRaw != null && typeof metaRaw === 'object' && !Array.isArray(metaRaw)
        ? (metaRaw as Record<string, unknown>)
        : null,
  };
}

export function parsePlanningResources(raw: unknown): PlanningResource[] {
  return asArray(unwrapEnvelope(raw))
    .map(parsePlanningResource)
    .filter((row): row is PlanningResource => row != null);
}

function buildWriteBody(
  request: CreatePlanningResourceRequest | UpdatePlanningResourceRequest,
  options?: { includeId?: boolean },
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    projectId: String(request.projectId ?? '').trim(),
    name: String(request.name ?? '').trim(),
    type: String(request.type ?? '').trim() || 'WORK',
    unitOfMeasure: String(request.unitOfMeasure ?? '').trim() || 'h',
    maxUnits: toNumber(request.maxUnits, 0),
    maxUnitsPerDay: toNumber(request.maxUnitsPerDay, 0),
    standardRate: toNumber(request.standardRate, 0),
    overtimeRate: toNumber(request.overtimeRate, 0),
    costPerUse: toNumber(request.costPerUse, 0),
    initials: String(request.initials ?? '').trim(),
    calendarId: String(request.calendarId ?? '').trim(),
  };

  if (options?.includeId && 'id' in request && request.id != null) {
    const id = String(request.id).trim();
    if (id) body.id = id;
  }

  if (request.email != null && String(request.email).trim() !== '') {
    body.email = String(request.email).trim();
  } else {
    body.email = null;
  }

  if (request.notes != null && String(request.notes).trim() !== '') {
    body.notes = String(request.notes);
  } else {
    body.notes = null;
  }

  if (request.meta != null && typeof request.meta === 'object') {
    body.meta = request.meta;
  }

  return body;
}

/** GET dpr/apis/planning/resource */
export async function fetchPlanningResources(
  projectId: string,
  options?: { silent?: boolean },
): Promise<PlanningResource[]> {
  const pid = String(projectId ?? '').trim();
  if (!pid) {
    throw new Error('projectId is required to fetch planning resources.');
  }

  try {
    const res = await transactionAxios.get('dpr/apis/planning/resource', {
      params: { projectId: pid },
    });
    return parsePlanningResources(res.data);
  } catch (err) {
    console.error(`Error fetching planning resources: ${err}`);
    if (!options?.silent) {
      showErrorNotification(err, `Sorry! Failed to fetch resources.`);
    }
    throw err;
  }
}

/** POST dpr/apis/planning/resource */
export async function createPlanningResource(
  request: CreatePlanningResourceRequest,
  options?: { silent?: boolean },
): Promise<unknown> {
  const projectId = String(request.projectId ?? '').trim();
  const name = String(request.name ?? '').trim();
  if (!projectId || !name) {
    throw new Error('projectId and name are required to create a planning resource.');
  }

  const body = buildWriteBody(request, { includeId: true });

  try {
    const res = await transactionAxios.post('dpr/apis/planning/resource', body, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!options?.silent) {
      notifications.show({
        title: 'Resource added',
        message: 'Resource saved successfully.',
        color: successNotificationColor,
      });
    }
    return res.data;
  } catch (err) {
    console.error(`Error creating planning resource: ${err}`);
    if (!options?.silent) {
      showErrorNotification(err, `Sorry! Failed to add resource.`);
    }
    throw err;
  }
}

/** PUT dpr/apis/planning/resource/{resourceId} */
export async function updatePlanningResource(
  resourceId: string,
  request: UpdatePlanningResourceRequest,
  options?: { silent?: boolean },
): Promise<unknown> {
  const id = String(resourceId ?? '').trim();
  const projectId = String(request.projectId ?? '').trim();
  if (!id || !projectId) {
    throw new Error('resourceId and projectId are required to update a planning resource.');
  }

  const body = buildWriteBody(request);

  try {
    const res = await transactionAxios.put(
      `dpr/apis/planning/resource/${encodeURIComponent(id)}`,
      body,
      { headers: { 'Content-Type': 'application/json' } },
    );
    if (!options?.silent) {
      notifications.show({
        title: 'Resource updated',
        message: 'Resource saved successfully.',
        color: successNotificationColor,
      });
    }
    return res.data;
  } catch (err) {
    console.error(`Error updating planning resource: ${err}`);
    if (!options?.silent) {
      showErrorNotification(err, `Sorry! Failed to update resource.`);
    }
    throw err;
  }
}

/** DELETE dpr/apis/planning/resource/{resourceId} */
export async function deletePlanningResource(
  resourceId: string,
  options?: { silent?: boolean },
): Promise<unknown> {
  const id = String(resourceId ?? '').trim();
  if (!id) {
    throw new Error('resourceId is required to delete a planning resource.');
  }

  try {
    const res = await transactionAxios.delete(
      `dpr/apis/planning/resource/${encodeURIComponent(id)}`,
    );
    if (!options?.silent) {
      notifications.show({
        title: 'Resource deleted',
        message: 'Resource removed successfully.',
        color: successNotificationColor,
      });
    }
    return res.data;
  } catch (err) {
    console.error(`Error deleting planning resource: ${err}`);
    if (!options?.silent) {
      showErrorNotification(err, `Sorry! Failed to delete resource.`);
    }
    throw err;
  }
}

export function toUpdatePlanningResourceRequest(
  resource: PlanningResource,
): UpdatePlanningResourceRequest {
  return {
    projectId: resource.projectId,
    name: resource.name,
    type: resource.type,
    unitOfMeasure: resource.unitOfMeasure,
    maxUnits: resource.maxUnits,
    maxUnitsPerDay: resource.maxUnitsPerDay,
    standardRate: resource.standardRate,
    overtimeRate: resource.overtimeRate,
    costPerUse: resource.costPerUse,
    initials: resource.initials,
    email: resource.email,
    calendarId: resource.calendarId,
    notes: resource.notes,
    meta: resource.meta,
  };
}
